import { z } from 'zod';
import { domainSchema } from './domain.schema';

const brandingSchema = z.object({
  colors: z.object({
    primary: z.string(),
    secondary: z.string(),
    accent: z.string().optional(),
    background: z.string().optional(),
    text: z.string().optional(),
  }),
  logo: z.object({
    url: z.string().min(1, 'Logo is required'),
    width: z.number().optional(),
    height: z.number().optional(),
    placement: z.enum(['header', 'footer', 'both']).default('header').optional(),
  }),
  favicon: z
    .object({
      url: z.string().optional(),
      width: z.number().optional(),
      height: z.number().optional(),
    })
    .optional(),
  fonts: z
    .object({
      primary: z.string().optional(),
      secondary: z.string().optional(),
    })
    .optional(),
  // ============================================
  // Extended Branding Sections
  // ============================================
  /** Team name displayed in hero section */
  teamName: z.string().optional(),
  /** Brokerage name for compliance and branding */
  brokerageName: z.string().optional(),
  /** Footer branding settings */
  footer: z
    .object({
      /** License information for the agent displayed in footer */
      license: z.string().optional(),
    })
    .optional(),
  /** Property details page branding settings */
  propertyDetails: z
    .object({
      /** Contact info or meeting link for property details page */
      contact: z.string().optional(),
      /** Tour scheduling info or meeting link */
      tour: z.string().optional(),
    })
    .optional(),
  /** Squeeze page branding settings with dynamic tag support */
  squeezePage: z
    .object({
      /** Custom opt-in language with support for dynamic tags */
      language: z.string().optional(),
    })
    .optional(),
});

// Define MLS details schema
const mlsDetailsSchema = z.object({
  boardCode: z.string(),
  agentId: z.array(z.string()).optional(),
  officeId: z.array(z.string()).optional(),
  enabled: z.boolean().optional(),
});

export const tenantSchema = z.object({
  id: z.string().min(1, 'Organization ID is required'),
  name: z.string().min(1, 'Name is required'),
  cities: z.array(z.string()).optional(),
  subscribedMLS: z.array(z.string()).optional(),
  subscribedMLSDetails: z.array(mlsDetailsSchema).optional(),
  domain: z.object({
    primary: domainSchema,
    aliases: z.array(z.string()),
  }),
  analytics: z.object({
    googleTag: z.string().optional(),
    gtmHead: z.string().optional(),
    gtmBody: z.string().optional(),
    gaHead: z.string().optional(),
  }),
  branding: brandingSchema,
  skipRentals: z.boolean().optional(),
  // SPEC-2232: per-org IDX sign-up consent treatment. Absent when the org's
  // column is unset, leaving the IDX site on its allowlist fallback.
  idxConsentStyle: z.enum(['passive', 'bundled', 'split']).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  organizationId: z.string(),
});

export type Tenant = z.infer<typeof tenantSchema>;

export const createTenantSchema = z.object({
  id: z.string().min(1, 'Organization ID is required'),
  name: z.string().min(1, 'Name is required'),
  domain: z.object({
    primary: domainSchema,
    aliases: z.array(z.string()),
  }),
  analytics: z.object({
    googleTag: z.string().optional(),
    gtmHead: z.string().optional(),
    gtmBody: z.string().optional(),
    gaHead: z.string().optional(),
  }),
  branding: brandingSchema,
});

export type CreateTenantRequest = z.infer<typeof createTenantSchema>;
export type CreateTenantData = z.infer<typeof createTenantSchema>;

export const updateTenantSchema = tenantSchema
  .pick({
    name: true,
    analytics: true,
    branding: true,
    cities: true,
    skipRentals: true,
  })
  .partial({
    branding: true,
  });
export type UpdateTenantData = z.infer<typeof updateTenantSchema>;
